import * as React from "react";

/**
 * When a timed toast closes, and when it waits - owned by the kit, not by Radix.
 *
 * Radix Toast 1.2.16 runs its own close timer, and two things in it cannot be fixed
 * from outside:
 * - The timer is never cleared when a toast unmounts. A toast replaced by a newer
 *   one under the same id still fires its old timer later and asks to close - and
 *   that request reached whatever was showing under the id by then: the "Saved"
 *   receipt's timer closed the failure that replaced it.
 * - Its pause flag is only reset by a resume event, and the listeners that send
 *   one are removed the moment the stack empties. Point at a toast and close it
 *   with its X, and every later toast waited, never closing, until the pointer
 *   happened to cross the stack again. A new toast that appeared while the stack
 *   was paused also had a countdown bar that ran while its timer did not.
 *
 * So Radix is handed `duration={Infinity}` (it never closes a toast on time) and the
 * kit keeps one pause state per stack, from the same signals Radix used: the pointer
 * over the stack, focus inside it, the window in the background. Every toast's timer
 * and its countdown bar read that one state, so they cannot drift apart, and the
 * state is reset whenever the stack empties.
 */

interface PauseState {
  /** True while the stack holds its toasts: pointer or focus inside, window blurred. */
  paused: boolean;
  /** Called by a toast while it is open; the returned function unregisters it. */
  register: () => () => void;
  /** Ref callback for the viewport element whose pointer and focus count. */
  bindViewport: (node: HTMLElement | null) => void;
}

const noop = () => {};
const ToastPauseContext = React.createContext<PauseState>({
  paused: false,
  register: () => noop,
  bindViewport: noop,
});

export function ToastPauseProvider({ children }: { children: React.ReactNode }) {
  const [paused, setPaused] = React.useState(false);
  const pointerInside = React.useRef(false);
  const focusInside = React.useRef(false);
  const windowBlurred = React.useRef(false);
  const openCount = React.useRef(0);
  const viewport = React.useRef<HTMLElement | null>(null);

  const recompute = React.useCallback(() => {
    setPaused(pointerInside.current || focusInside.current || windowBlurred.current);
  }, []);

  const register = React.useCallback(() => {
    openCount.current += 1;
    return () => {
      openCount.current -= 1;
      // An empty stack is holding nothing. Whatever the pointer or focus did to the
      // last toast, the next one starts its count as soon as it appears.
      if (openCount.current === 0) {
        pointerInside.current = false;
        focusInside.current = false;
        recompute();
      }
    };
  }, [recompute]);

  const listeners = React.useRef<(() => void) | null>(null);
  const bindViewport = React.useCallback(
    (node: HTMLElement | null) => {
      listeners.current?.();
      listeners.current = null;
      viewport.current = node;
      if (!node) return;
      const onPointerEnter = () => {
        pointerInside.current = true;
        recompute();
      };
      const onPointerLeave = () => {
        pointerInside.current = false;
        recompute();
      };
      const onFocusIn = () => {
        focusInside.current = true;
        recompute();
      };
      const onFocusOut = (event: FocusEvent) => {
        if (event.relatedTarget instanceof Node && node.contains(event.relatedTarget)) return;
        focusInside.current = false;
        recompute();
      };
      const onBlur = () => {
        windowBlurred.current = true;
        recompute();
      };
      const onFocus = () => {
        windowBlurred.current = false;
        recompute();
      };
      node.addEventListener("pointerenter", onPointerEnter);
      node.addEventListener("pointerleave", onPointerLeave);
      node.addEventListener("focusin", onFocusIn);
      node.addEventListener("focusout", onFocusOut);
      window.addEventListener("blur", onBlur);
      window.addEventListener("focus", onFocus);
      listeners.current = () => {
        node.removeEventListener("pointerenter", onPointerEnter);
        node.removeEventListener("pointerleave", onPointerLeave);
        node.removeEventListener("focusin", onFocusIn);
        node.removeEventListener("focusout", onFocusOut);
        window.removeEventListener("blur", onBlur);
        window.removeEventListener("focus", onFocus);
      };
    },
    [recompute],
  );

  const value = React.useMemo(() => ({ paused, register, bindViewport }), [paused, register, bindViewport]);
  return React.createElement(ToastPauseContext.Provider, { value }, children);
}

export function useToastPause(): PauseState {
  return React.useContext(ToastPauseContext);
}

/**
 * Close a timed toast after `duration` milliseconds of not being paused.
 *
 * The count restarts when the toast opens again or its duration changes, holds while
 * `paused`, and is dropped with the toast: an unmounted toast can no longer ask
 * anything to close. `Infinity` never expires.
 */
export function useToastTimer(open: boolean, duration: number, paused: boolean, onExpire: () => void) {
  const remaining = React.useRef(duration);
  const expire = React.useRef(onExpire);
  expire.current = onExpire;

  React.useEffect(() => {
    remaining.current = duration;
  }, [open, duration]);

  React.useEffect(() => {
    if (!open || paused || !Number.isFinite(duration)) return;
    const started = Date.now();
    const id = window.setTimeout(() => expire.current(), Math.max(0, remaining.current));
    return () => {
      window.clearTimeout(id);
      remaining.current -= Date.now() - started;
    };
  }, [open, duration, paused]);
}
